import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { IS_GAME_MODE } from './lib/mode';

const API_BASE = process.env.REACT_APP_API_URL || 'https://norwich-hub.onrender.com';
const MAX_ITEMS = 12;

interface TickerItem { id: number; text: string; isAlert?: boolean; }

let _seq = 0;
function uid() { return ++_seq; }

const USER_LABEL: Record<string, string> = {
  alice:   'ALICE',
  bob:     'BOB',
  charlie: 'CHARLIE',
};
function label(userId: string): string { return USER_LABEL[userId] ?? userId.toUpperCase(); }

export interface ProphecyAlert { label: string; gap: number; }

interface Props {
  apiBase?:        string;
  mode?:           'binary' | 'multi';
  prophecyAlerts?: ProphecyAlert[]; // pre-seeded alerts from current priceData
}

const FEE_MSG = IS_GAME_MODE
  ? 'AGORACLE ARENA: 3% AGORA LEVY ON NET WINNINGS ONLY · 50% PLATFORM · 25% CREATOR · 25% ORACLE POOL · NO TRADE FEE · ACCURACY PAYS!'
  : 'NORWICH HUB: 3% AGORA LEVY ON NET WINNINGS ONLY · 50% PLATFORM · 25% CREATOR · 25% ORACLE POOL · NO TRADE FEE · ACCURACY PAYS!';

export default function GlobalTicker({ apiBase = API_BASE, mode = 'binary', prophecyAlerts = [] }: Props): React.JSX.Element {
  const buildSeed = (): TickerItem[] => {
    const base: TickerItem[] = [
      { id: uid(), text: IS_GAME_MODE
        ? (mode === 'multi'
            ? 'AGORACLE ARENA · MULTI-OUTCOME NERVE CENTER · AWAITING ACTIVITY'
            : 'AGORACLE ARENA · PREDICTION MARKET EXCHANGE · AWAITING ACTIVITY')
        : (mode === 'multi'
            ? 'NORWICH HUB LIVE · MULTI-OUTCOME NERVE CENTER · AWAITING ACTIVITY'
            : 'NORWICH HUB LIVE · PREDICTION MARKET EXCHANGE · AWAITING ACTIVITY') },
      { id: uid(), text: FEE_MSG },
    ];
    if (prophecyAlerts.length > 0) {
      const alerts = prophecyAlerts.map(a => ({
        id:      uid(),
        text:    `⚡ PROPHECY ALERT: ${a.label.toUpperCase()} — Divergence +${(a.gap * 100).toFixed(1)}%`,
        isAlert: true,
      }));
      return [...alerts, ...base];
    }
    return base;
  };

  const [items, setItems] = useState<TickerItem[]>(buildSeed);
  const cancelled  = useRef(false);
  const betCount   = useRef(0);

  // Refresh seed alerts when priceData changes
  useEffect(() => {
    if (prophecyAlerts.length > 0) {
      setItems(prophecyAlerts.map(a => ({
        id:      uid(),
        text:    `⚡ PROPHECY ALERT: ${a.label.toUpperCase()} — Divergence +${(a.gap * 100).toFixed(1)}%`,
        isAlert: true,
      })));
    }
  }, [prophecyAlerts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    cancelled.current = false;
    const socket = io(apiBase);

    function push(text: string, isAlert = false) {
      if (cancelled.current) return;
      setItems(prev => [{ id: uid(), text, isAlert }, ...prev].slice(0, MAX_ITEMS));
    }

    if (mode === 'binary') {
      socket.on('new_bet', (d: { user_id: string; side: string; amount: number }) => {
        push(`[${label(d.user_id)}] bought ${d.side} for $${Number(d.amount).toFixed(2)}`);
      });
      socket.on('market_created', (d: { creator_id: string; title: string }) => {
        push(`[${label(d.creator_id)}] created market '${d.title}'`);
      });
      socket.on('market_resolved', (d: { winning_outcome: string }) => {
        push(`MARKET SETTLED · ${d.winning_outcome} WON`);
      });
    }

    if (mode === 'multi') {
      socket.on('multi_bet', (d: { user_id: string; outcome_label: string; shares: number; cost: number }) => {
        betCount.current += 1;
        push(`[${label(d.user_id)}] staked ${d.shares} truth units of ${d.outcome_label.toUpperCase()} · $${Number(d.cost).toFixed(2)}`);
        if (betCount.current % 5 === 0) push(FEE_MSG);
      });
      socket.on('divergence_points', (d: { user_id: string; dp_earned: number; multiplier?: number }) => {
        const mult = d.multiplier && d.multiplier >= 2 ? ` · ${d.multiplier.toFixed(1)}× Multiplier` : '';
        push(`⚡ [${label(d.user_id)}] earned +${d.dp_earned.toFixed(2)} DP${mult}`, true);
      });
      socket.on('vibe_update', (d: { outcome_id: string; composite_vibe?: number }) => {
        if (d.composite_vibe !== undefined) {
          push(`ORACLE UPDATE · Vibe now ${(d.composite_vibe * 100).toFixed(0)}%`);
        }
      });
    }

    return () => { cancelled.current = true; socket.disconnect(); };
  }, [apiBase, mode]);

  // Build scrolling text — alerts highlighted with marker
  const parts = items.map(it => it.isAlert ? `⚡ ${it.text} ⚡` : it.text);
  const tickerText = parts.join('  ···  ');
  const content    = `+++ ${tickerText}  +++  ${tickerText}  +++`;

  // Color: gold for any alert items present, default teal otherwise
  const hasAlert   = items.some(it => it.isAlert);
  const textColor  = hasAlert ? '#92400e' : '#4b6080';
  const borderColor = hasAlert ? '#78350f' : '#1a2a3a';

  return (
    <div style={{
      width:           '100%',
      backgroundColor: hasAlert ? '#0d0800' : '#070d12',
      borderTop:       `1px solid ${borderColor}`,
      borderBottom:    `1px solid ${borderColor}`,
      overflow:        'hidden',
      padding:         '5px 0',
      transition:      'background-color 0.5s, border-color 0.5s',
    }}>
      <style>{`
        @keyframes marqueeScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display:     inline-block;
          white-space: nowrap;
          animation:   marqueeScroll 45s linear infinite;
          will-change: transform;
        }
        .ticker-track:hover { animation-play-state: paused; }
      `}</style>
      <div className="ticker-track" style={{ fontFamily: 'monospace', fontSize: 10, color: textColor, letterSpacing: '0.06em' }}>
        {content}
      </div>
    </div>
  );
}
